import { z } from 'zod';

/**
 * Webhook subscription DTOs.
 *
 * Event names follow `<resource>.<action>` (e.g. `task.created`,
 * `milestone.updated`).  Wildcards `task.*` subscribe to every action
 * for a resource.  The full catalogue is documented in
 * docs/integrations/webhooks.md.
 */

const WebhookName = z.string().min(1).max(80);
const WebhookUrl = z
  .string()
  .url()
  .refine((u) => /^https:\/\/[^/?#@:]+(?::443)?(?:[/?#]|$)/i.test(u), {
    message: 'URL must use HTTPS on port 443 without credentials',
  });
const EventName = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_*]+)+$/, {
    message: 'Event name must look like "task.created" or "task.*"',
  });

export const WebhookSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  name: z.string(),
  url: z.string(),
  events: z.array(z.string()),
  isActive: z.boolean(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
  deliveryCountTotal: z.number().int().nonnegative(),
  deliveryCountFailed: z.number().int().nonnegative(),
  lastDeliveryAt: z.string().nullable(),
});
export type WebhookProjection = z.infer<typeof WebhookSchema>;
export const WebhookListSchema = z.array(WebhookSchema);

/** Returned by POST /webhooks — carries the plaintext secret once. */
export const CreatedWebhookSchema = WebhookSchema.extend({
  secret: z.string(),
});
export type CreatedWebhookProjection = z.infer<typeof CreatedWebhookSchema>;

export const CreateWebhookSchema = z
  .object({
    name: WebhookName,
    url: WebhookUrl,
    events: z.array(EventName).min(1).max(50),
  })
  .strict();
export type CreateWebhookInput = z.infer<typeof CreateWebhookSchema>;

export const UpdateWebhookSchema = z
  .object({
    name: WebhookName.optional(),
    url: WebhookUrl.optional(),
    events: z.array(EventName).min(1).max(50).optional(),
    isActive: z.boolean().optional(),
  })
  .strict();
export type UpdateWebhookInput = z.infer<typeof UpdateWebhookSchema>;

// ── Delivery projection (recent deliveries panel) ──────────────────────────

export const WebhookDeliverySchema = z.object({
  id: z.string().uuid(),
  webhookId: z.string().uuid(),
  eventType: z.string(),
  status: z.enum(['pending', 'delivered', 'failed']),
  attempts: z.number().int().nonnegative(),
  responseStatus: z.number().int().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  deliveredAt: z.string().nullable(),
  /** Request body sent to the subscriber — JSONB payload Projoint signed and POSTed. */
  payload: z.unknown(),
  /** Response body from the subscriber, bounded to 2000 bytes.  Null until the first attempt completes. */
  responseBody: z.string().nullable(),
  /** When the next retry is scheduled for `pending` rows; equal to last attempt time otherwise. */
  nextAttemptAt: z.string(),
});
export type WebhookDeliveryProjection = z.infer<typeof WebhookDeliverySchema>;
export const WebhookDeliveryListSchema = z.array(WebhookDeliverySchema);
